import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import api from '../services/api';

const Checkout = () => {
  const navigate = useNavigate();
  const { cartItems, cartTotal, clearCart } = useContext(CartContext);
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    direccion: '',
    ciudad: '',
    estado: '',
    cp: ''
  });

  const [envio, setEnvio] = useState({
    cotizando: false,
    cotizaciones: [],
    seleccionado: null,
    error: null
  });

  const [comprobante, setComprobante] = useState(null);
  const [procesando, setProcesando] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderData, setOrderData] = useState(null);

  const subtotal = cartTotal || 0;
  const pesoTotal = cartItems ? cartItems.reduce((acc, item) => acc + ((item.quantity || 1) * 0.5), 0) : 0; // 0.5kg por producto

  // Cotizar cuando cambia el CP
  useEffect(() => {
    const cotizarEnvio = async () => {
      if (formData.cp && formData.cp.length === 5) {
        setEnvio(prev => ({ ...prev, cotizando: true, error: null }));
        
        try {
          const response = await api.post('/envios/cotizar', {
            cp_destino: formData.cp,
            peso: Math.max(1, pesoTotal),
            valor_declarado: subtotal,
            largo: 30,
            alto: 20,
            ancho: 15,
            nombre: `${formData.nombre} ${formData.apellido}`,
            telefono: formData.telefono,
            direccion: formData.direccion,
            ciudad: formData.ciudad,
            estado: formData.estado
          });

          if (response.data.success) {
            setEnvio(prev => ({
              ...prev,
              cotizando: false,
              cotizaciones: response.data.data.cotizaciones,
              seleccionado: response.data.data.mas_economica
            }));
          } else {
            setEnvio(prev => ({
              ...prev,
              cotizando: false,
              error: response.data.error || 'No se pudo cotizar'
            }));
          }
        } catch (error) {
          setEnvio(prev => ({
            ...prev,
            cotizando: false,
            error: 'Error al conectar con el servidor'
          }));
        }
      }
    };

    const timer = setTimeout(() => {
      if (formData.cp?.length === 5) {
        cotizarEnvio();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.cp, pesoTotal, subtotal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setComprobante(e.target.files[0]);
  };

  const seleccionarEnvio = (opcion) => {
    setEnvio(prev => ({ ...prev, seleccionado: opcion }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!envio.seleccionado) {
      alert('Por favor selecciona una opción de envío');
      return;
    }
    if (!comprobante) {
      alert('Debes subir el comprobante de pago');
      return;
    }

    setProcesando(true);

    try {
      // 1. Subir comprobante
      const formDataUpload = new FormData();
      formDataUpload.append('comprobante', comprobante);

      const uploadRes = await api.post('/upload/comprobante', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!uploadRes.data.success) {
        throw new Error('Error al subir comprobante');
      }

      const comprobanteUrl = uploadRes.data.url;

      // 2. Crear venta
      const pedidoData = {
        cliente: {
          nombre: `${formData.nombre} ${formData.apellido}`,
          email: formData.email,
          telefono: formData.telefono,
          direccion: formData.direccion,
          ciudad: formData.ciudad,
          estado: formData.estado,
          cp: formData.cp,
          pais: 'MX'
        },
        productos: cartItems.map(item => ({
          id: item.id,
          nombre: item.name,
          cantidad: item.quantity,
          precio: item.price
        })),
        subtotal: subtotal,
        envio: {
          carrier: envio.seleccionado.carrier,
          service: envio.seleccionado.service,
          carrierId: envio.seleccionado.carrierId,
          serviceId: envio.seleccionado.serviceId,
          precio: envio.seleccionado.price,
          pesoTotal: pesoTotal,
          cp_destino: formData.cp
        },
        total: subtotal + envio.seleccionado.price,
        metodo_pago: 'transferencia',
        comprobante_url: comprobanteUrl
      };

      console.log('📦 Enviando pedido:', pedidoData);

      const response = await api.post('/ventas', pedidoData);

      if (response.data.success) {
        setOrderData({
          folio: response.data.folio,
          guia: response.data.guia,
          total: subtotal + envio.seleccionado.price
        });
        setOrderPlaced(true);
        clearCart();
        setTimeout(() => navigate('/products'), 8000);
      } else {
        alert('Error al procesar el pedido');
      }
    } catch (error) {
      console.error('❌ Error en checkout:', error);
      alert('Ocurrió un error: ' + (error.response?.data?.error || error.message));
    } finally {
      setProcesando(false);
    }
  };

  // ESTILOS COMPARTIDOS
  const styles = `
    :root {
      --primary-color: #8B4513;
      --primary-hover: #A0522D;
      --secondary-color: #D2B48C;
      --dark-bg: #1A1A1A;
      --light-bg: #F9F6F0;
      --text-main: #2C2C2C;
      --text-light: #666666;
      --white: #FFFFFF;
      --radius-md: 12px;
      --radius-lg: 24px;
      --shadow-sm: 0 4px 6px rgba(0, 0, 0, 0.05);
      --shadow-md: 0 10px 30px rgba(0, 0, 0, 0.08);
      --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .checkout-page-wrapper {
      background-color: var(--light-bg);
      min-height: 80vh;
      padding: 4rem 5%;
      font-family: 'Inter', -apple-system, sans-serif;
      color: var(--text-main);
    }

    .checkout-header {
      text-align: center;
      margin-bottom: 3rem;
    }

    .checkout-header h1 {
      font-family: 'Playfair Display', serif;
      font-size: 3rem;
      color: var(--dark-bg);
      margin-bottom: 0.5rem;
    }

    .checkout-header p {
      color: var(--text-light);
      font-size: 1.1rem;
    }

    .checkout-container {
      max-width: 1200px;
      margin: 0 auto;
      display: grid;
      grid-template-columns: 1.2fr 400px;
      gap: 3rem;
      align-items: start;
    }

    /* FORM STYLES */
    .form-section {
      background: var(--white);
      padding: 2.5rem;
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-sm);
      margin-bottom: 2rem;
      border: 1px solid rgba(0,0,0,0.05);
    }

    .form-section h2 {
      font-family: 'Playfair Display', serif;
      font-size: 1.8rem;
      color: var(--dark-bg);
      margin-bottom: 2rem;
      border-bottom: 1px solid #EEE;
      padding-bottom: 1rem;
    }

    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1.5rem;
      margin-bottom: 1.5rem;
    }

    .form-group {
      margin-bottom: 1.5rem;
      position: relative;
    }

    .form-group label {
      display: block;
      margin-bottom: 0.5rem;
      font-weight: 600;
      color: var(--text-main);
      font-size: 0.95rem;
    }

    .form-group input, 
    .form-group select {
      width: 100%;
      padding: 1rem 1.25rem;
      border: 1px solid #E0E0E0;
      border-radius: var(--radius-md);
      background: #FAFAFA;
      font-family: inherit;
      font-size: 1rem;
      transition: var(--transition);
      color: var(--text-main);
    }

    .form-group input:focus, 
    .form-group select:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 3px rgba(139, 69, 19, 0.1);
      background: var(--white);
    }

    .form-group input.valido {
      border-color: #4CAF50;
      background: #F1F8E9;
    }

    .form-group input.error {
      border-color: #F44336;
      background: #FFEBEE;
    }

    .cotizando {
      position: absolute;
      bottom: -20px;
      left: 0;
      font-size: 0.85rem;
      color: var(--primary-color);
      font-weight: bold;
    }

    .error {
      position: absolute;
      bottom: -20px;
      left: 0;
      font-size: 0.85rem;
      color: #F44336;
      font-weight: bold;
    }

    /* SHIPPING OPTIONS CARDS */
    .opciones-envio {
      margin-top: 2rem;
      background: var(--light-bg);
      padding: 1.5rem;
      border-radius: var(--radius-md);
    }

    .opciones-envio h3 {
      font-size: 1.2rem;
      margin-bottom: 1.5rem;
      color: var(--dark-bg);
    }

    .opcion-envio {
      background: var(--white);
      border: 2px solid #E0E0E0;
      border-radius: var(--radius-md);
      padding: 1.25rem;
      margin-bottom: 1rem;
      cursor: pointer;
      transition: var(--transition);
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .opcion-envio:hover {
      border-color: var(--secondary-color);
    }

    .opcion-envio.seleccionado {
      border-color: var(--primary-color);
      background: #FFF8F0;
      box-shadow: 0 4px 12px rgba(139, 69, 19, 0.1);
    }

    .opcion-header {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .opcion-header input[type="radio"] {
      width: 20px;
      height: 20px;
      accent-color: var(--primary-color);
      cursor: pointer;
    }

    .carrier {
      font-weight: 800;
      color: var(--dark-bg);
      text-transform: uppercase;
      font-size: 1.1rem;
    }

    .servicio {
      color: var(--text-light);
      font-size: 0.95rem;
    }

    .opcion-body {
      padding-left: 2.5rem; /* Alinear con el texto al lado del radio */
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .opcion-body .precio {
      font-size: 1.25rem;
      font-weight: 800;
      color: var(--primary-color);
    }

    .opcion-body .tiempo {
      font-size: 0.9rem;
      color: var(--text-main);
      background: var(--light-bg);
      padding: 0.3rem 0.8rem;
      border-radius: 20px;
      font-weight: 600;
    }

    /* PAYMENT METHOD */
    .payment-methods {
      margin-bottom: 2rem;
    }

    .payment-method {
      display: flex;
      align-items: center;
      gap: 1rem;
      background: #FFF8F0;
      border: 2px solid var(--primary-color);
      padding: 1.5rem;
      border-radius: var(--radius-md);
      cursor: pointer;
    }

    .payment-method input[type="radio"] {
      width: 20px;
      height: 20px;
      accent-color: var(--primary-color);
    }

    .payment-method span {
      font-weight: 700;
      font-size: 1.1rem;
      color: var(--dark-bg);
    }

    .form-group input[type="file"] {
      padding: 1rem;
      background: #FFFFFF;
      border: 1px dashed var(--primary-color);
      cursor: pointer;
    }

    .form-group small {
      display: block;
      margin-top: 0.5rem;
      color: var(--text-light);
    }

    .place-order-btn {
      width: 100%;
      background: var(--primary-color);
      color: var(--white);
      border: none;
      padding: 1.5rem;
      border-radius: 50px;
      font-size: 1.25rem;
      font-weight: 800;
      cursor: pointer;
      transition: var(--transition);
      box-shadow: var(--shadow-md);
      margin-top: 1rem;
    }

    .place-order-btn:hover:not(:disabled) {
      background: var(--dark-bg);
      transform: translateY(-3px);
      box-shadow: var(--shadow-lg);
    }

    .place-order-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      background: #A0A0A0;
    }

    /* ORDER SUMMARY (Sidebar) */
    .order-summary {
      background: var(--dark-bg);
      color: var(--white);
      padding: 2.5rem;
      border-radius: var(--radius-lg);
      position: sticky;
      top: 2rem;
      box-shadow: var(--shadow-lg);
    }

    .order-summary h2 {
      font-family: 'Playfair Display', serif;
      font-size: 1.8rem;
      margin-bottom: 2rem;
      border-bottom: 1px solid rgba(255,255,255,0.1);
      padding-bottom: 1rem;
      color: var(--secondary-color);
    }

    .summary-items {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin-bottom: 2rem;
      max-height: 300px;
      overflow-y: auto;
      padding-right: 0.5rem;
    }

    /* Scrollbar styling for summary items */
    .summary-items::-webkit-scrollbar { width: 6px; }
    .summary-items::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); border-radius: 10px; }
    .summary-items::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 10px; }

    .summary-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 1rem;
      border-bottom: 1px dashed rgba(255,255,255,0.1);
    }

    .summary-item:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    .item-name {
      color: #E0E0E0;
      font-size: 0.95rem;
      line-height: 1.4;
      padding-right: 1rem;
    }

    .item-price {
      font-weight: 600;
      white-space: nowrap;
    }

    .summary-totals {
      border-top: 1px solid rgba(255,255,255,0.2);
      padding-top: 1.5rem;
    }

    .summary-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 1rem;
      font-size: 1.05rem;
      color: #E0E0E0;
    }

    .summary-row.envio {
      color: var(--secondary-color);
    }

    .summary-row.total {
      border-top: 1px solid rgba(255,255,255,0.2);
      padding-top: 1.5rem;
      margin-top: 1rem;
      margin-bottom: 0;
      font-size: 1.6rem;
      font-weight: 800;
      color: var(--white);
    }

    /* STATES (Empty & Success) */
    .empty-state-page, .success-state-page {
      min-height: 70vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: var(--light-bg);
      padding: 2rem;
    }

    .status-card {
      background: var(--white);
      padding: 4rem 3rem;
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-md);
      text-align: center;
      max-width: 600px;
      width: 100%;
      border: 1px solid rgba(139, 69, 19, 0.1);
    }

    .status-icon {
      font-size: 5rem;
      display: block;
      margin-bottom: 1.5rem;
      color: var(--secondary-color);
    }

    .success-icon {
      width: 100px;
      height: 100px;
      background: #4CAF50;
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 4rem;
      margin: 0 auto 2rem;
      box-shadow: 0 10px 20px rgba(76, 175, 80, 0.3);
      animation: popIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
    }

    @keyframes popIn {
      0% { transform: scale(0); }
      100% { transform: scale(1); }
    }

    .status-card h2 {
      font-family: 'Playfair Display', serif;
      font-size: 2.5rem;
      color: var(--dark-bg);
      margin-bottom: 1rem;
    }

    .status-card p {
      color: var(--text-light);
      margin-bottom: 2rem;
      font-size: 1.1rem;
      line-height: 1.6;
    }

    .order-details-box {
      background: var(--light-bg);
      padding: 2rem;
      border-radius: var(--radius-md);
      margin-bottom: 2rem;
      text-align: left;
    }

    .order-details-box p {
      margin-bottom: 0.8rem;
      color: var(--text-main);
      display: flex;
      justify-content: space-between;
      border-bottom: 1px dashed #CCC;
      padding-bottom: 0.8rem;
    }

    .order-details-box p:last-child {
      border-bottom: none;
      margin-bottom: 0;
      padding-bottom: 0;
      font-size: 1.2rem;
      font-weight: 800;
      color: var(--primary-color);
    }

    .btn-return {
      display: inline-block;
      background-color: var(--primary-color);
      color: var(--white);
      padding: 1rem 3rem;
      border-radius: 50px;
      text-decoration: none;
      font-weight: 700;
      transition: var(--transition);
    }

    .btn-return:hover {
      background-color: var(--dark-bg);
      transform: translateY(-3px);
      box-shadow: var(--shadow-md);
    }

    @media (max-width: 968px) {
      .checkout-container { grid-template-columns: 1fr; }
      .form-row { grid-template-columns: 1fr; }
      .order-summary { position: relative; top: 0; }
    }
  `;

  // PANTALLA: CARRITO VACÍO
  if (!cartItems || cartItems.length === 0) {
    return (
      <>
        <style>{styles}</style>
        <div className="empty-state-page">
          <div className="status-card">
            <span className="status-icon">🛒</span>
            <h2>No hay productos a comprar</h2>
            <p>Tu carrito está vacío. Regresa a la tienda para agregar nuestros deliciosos cafés y productos artesanales.</p>
            <Link to="/products" className="btn-return">Explorar Catálogo</Link>
          </div>
        </div>
      </>
    );
  }

  // PANTALLA: PEDIDO CONFIRMADO
  if (orderPlaced) {
    return (
      <>
        <style>{styles}</style>
        <div className="success-state-page">
          <div className="status-card">
            <div className="success-icon">✓</div>
            <h2>¡Pedido Confirmado!</h2>
            <p>Hemos recibido tu comprobante de pago y tu solicitud de pedido con éxito. Comenzaremos a procesarlo de inmediato.</p>
            
            {orderData && (
              <div className="order-details-box">
                <p><span>Folio de Pedido:</span> <strong>{orderData.folio}</strong></p>
                {orderData.guia && <p><span>Guía de Rastreo:</span> <strong>{orderData.guia}</strong></p>}
                <p><span>Total Pagado:</span> <strong>${orderData.total.toFixed(2)} MXN</strong></p>
              </div>
            )}
            
            <p style={{fontSize: '0.9rem', marginBottom: '2rem'}}>Te redirigiremos automáticamente a la tienda en unos segundos...</p>
            <Link to="/products" className="btn-return">Volver al Catálogo</Link>
          </div>
        </div>
      </>
    );
  }

  // PANTALLA PRINCIPAL: CHECKOUT
  return (
    <>
      <style>{styles}</style>
      <div className="checkout-page-wrapper">
        <div className="checkout-header">
          <h1>Finalizar Compra</h1>
          <p>Completa tus datos de envío y pago para procesar tu pedido</p>
        </div>

        <div className="checkout-container">
          <div className="checkout-form-container">
            <form onSubmit={handleSubmit} className="checkout-form">
              
              {/* SECCIÓN 1: DATOS PERSONALES */}
              <div className="form-section">
                <h2>1. Datos Personales</h2>
                <div className="form-row">
                  <div className="form-group">
                    <label>Nombre *</label>
                    <input type="text" name="nombre" value={formData.nombre} onChange={handleInputChange} required placeholder="Ej: Juan" />
                  </div>
                  <div className="form-group">
                    <label>Apellido *</label>
                    <input type="text" name="apellido" value={formData.apellido} onChange={handleInputChange} required placeholder="Ej: Pérez" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Email *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="correo@ejemplo.com" />
                  </div>
                  <div className="form-group">
                    <label>Teléfono *</label>
                    <input type="tel" name="telefono" value={formData.telefono} onChange={handleInputChange} required placeholder="10 dígitos" />
                  </div>
                </div>
              </div>

              {/* SECCIÓN 2: DIRECCIÓN Y ENVÍO */}
              <div className="form-section">
                <h2>2. Dirección de Envío</h2>
                <div className="form-group">
                  <label>Dirección completa (Calle, Número, Colonia) *</label>
                  <input type="text" name="direccion" value={formData.direccion} onChange={handleInputChange} required placeholder="Ej: Av. Reforma 123, Col. Centro" />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Ciudad *</label>
                    <input type="text" name="ciudad" value={formData.ciudad} onChange={handleInputChange} required placeholder="Ej: Puebla" />
                  </div>
                  <div className="form-group">
                    <label>Estado *</label>
                    <select name="estado" value={formData.estado} onChange={handleInputChange} required>
                      <option value="">Selecciona un estado</option>
                      <option value="AGS">Aguascalientes</option>
                      <option value="BC">Baja California</option>
                      <option value="BCS">Baja California Sur</option>
                      <option value="CAMP">Campeche</option>
                      <option value="COAH">Coahuila</option>
                      <option value="COL">Colima</option>
                      <option value="CHIS">Chiapas</option>
                      <option value="CHIH">Chihuahua</option>
                      <option value="CDMX">Ciudad de México</option>
                      <option value="DGO">Durango</option>
                      <option value="GTO">Guanajuato</option>
                      <option value="GRO">Guerrero</option>
                      <option value="HGO">Hidalgo</option>
                      <option value="JAL">Jalisco</option>
                      <option value="MEX">Estado de México</option>
                      <option value="MICH">Michoacán</option>
                      <option value="MOR">Morelos</option>
                      <option value="NAY">Nayarit</option>
                      <option value="NL">Nuevo León</option>
                      <option value="OAX">Oaxaca</option>
                      <option value="PUE">Puebla</option>
                      <option value="QRO">Querétaro</option>
                      <option value="QROO">Quintana Roo</option>
                      <option value="SLP">San Luis Potosí</option>
                      <option value="SIN">Sinaloa</option>
                      <option value="SON">Sonora</option>
                      <option value="TAB">Tabasco</option>
                      <option value="TAMPS">Tamaulipas</option>
                      <option value="TLAX">Tlaxcala</option>
                      <option value="VER">Veracruz</option>
                      <option value="YUC">Yucatán</option>
                      <option value="ZAC">Zacatecas</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Código Postal (Calcula el envío) *</label>
                    <input
                      type="text"
                      name="cp"
                      value={formData.cp}
                      onChange={handleInputChange}
                      maxLength="5"
                      pattern="[0-9]{5}"
                      required
                      placeholder="Ingresa 5 dígitos"
                      className={envio.cotizaciones.length > 0 ? 'valido' : envio.error ? 'error' : ''}
                    />
                    {envio.cotizando && <small className="cotizando">⏳ Cotizando opciones de envío...</small>}
                    {envio.error && <small className="error">❌ {envio.error}</small>}
                  </div>
                </div>

                {/* COTIZADOR VISUAL DE ENVÍOS */}
                {envio.cotizaciones.length > 0 && (
                  <div className="opciones-envio">
                    <h3>Selecciona tu paquetería</h3>
                    {envio.cotizaciones.map((opcion, idx) => (
                      <label
                        key={idx}
                        className={`opcion-envio ${envio.seleccionado === opcion ? 'seleccionado' : ''}`}
                      >
                        <div className="opcion-header">
                          <input
                            type="radio"
                            name="envio_seleccion"
                            checked={envio.seleccionado === opcion}
                            onChange={() => seleccionarEnvio(opcion)}
                          />
                          <span className="carrier">{opcion.carrier}</span>
                          <span className="servicio">• {opcion.description}</span>
                        </div>
                        <div className="opcion-body">
                          <span className="precio">${opcion.price} MXN</span>
                          <span className="tiempo">Llega en {opcion.estimatedDays} días aprox.</span>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* SECCIÓN 3: PAGO */}
              <div className="form-section">
                <h2>3. Método de Pago</h2>
                <div className="payment-methods">
                  <label className="payment-method">
                    <input type="radio" name="metodoPago" value="transferencia" checked={true} readOnly />
                    <span>🏦 Transferencia o Depósito Bancario</span>
                  </label>
                </div>
                
                <div className="form-group" style={{marginTop: '1.5rem'}}>
                  <label>Sube tu comprobante de pago (Imagen o PDF) *</label>
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handleFileChange}
                    required
                  />
                  <small>Una vez validado el comprobante, procesaremos tu envío inmediatamente.</small>
                </div>
              </div>

              <button 
                type="submit" 
                className="place-order-btn" 
                disabled={procesando || !envio.seleccionado || !comprobante}
              >
                {procesando ? 'Procesando tu pedido...' : 'Confirmar Pedido y Pagar'}
              </button>
            </form>
          </div>

          {/* SIDEBAR: RESUMEN DEL PEDIDO */}
          <div className="checkout-summary-wrapper">
            <div className="order-summary">
              <h2>Resumen del Pedido</h2>
              
              <div className="summary-items">
                {cartItems.map(item => (
                  <div key={item.id} className="summary-item">
                    <span className="item-name">
                      {item.name} <strong style={{color: 'var(--secondary-color)'}}>x{item.quantity}</strong>
                    </span>
                    <span className="item-price">${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              
              <div className="summary-totals">
                <div className="summary-row">
                  <span>Subtotal productos:</span>
                  <span>${subtotal.toFixed(2)} MXN</span>
                </div>
                <div className="summary-row envio">
                  <span>Costo de Envío:</span>
                  <span>
                    {envio.seleccionado 
                      ? `+$${envio.seleccionado.price.toFixed(2)} MXN` 
                      : 'Por cotizar'}
                  </span>
                </div>
                <div className="summary-row total">
                  <span>Total a Pagar:</span>
                  <span>${(subtotal + (envio.seleccionado?.price || 0)).toFixed(2)} MXN</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Checkout;